import { tx } from "@/i18n/tx";
import { catLabel } from "@/components/invoices/BatchReviewSheet";
import { Check, Ban, Copy, Repeat, Undo2, AlertCircle } from "lucide-react";
import { fmtCHF, fmtDate } from "@/lib/api";

const STATUS_LABEL = {
  retard: { txt: "En retard", cls: "status-retard" },
  bientot_du: { txt: tx("ui_invoicecard.kae53a6"), cls: "status-bientot" },
  en_attente: { txt: "En attente", cls: "status-attente" },
  payee: { txt: tx("ui_invoicecard.ke2567e"), cls: "status-payee" },
  annulee: { txt: tx("ui_invoicecard.k07b6e0"), cls: "bg-[#EFEDE8] text-[#5C5C5C]" },
};

const Btn = ({ children, className = "", ...p }) => (
  <button {...p} className={`h-10 px-3 rounded-xl text-sm font-semibold flex items-center gap-1.5 ${className}`}>{children}</button>
);

export default function InvoiceCard({ inv, onAction, onDuplicate }) {
  const s = STATUS_LABEL[inv.status] || STATUS_LABEL.en_attente;
  const cancelled = inv.status === "annulee";
  return (
    <li className={`card-c !p-4 flex flex-col ${cancelled ? "opacity-75" : ""}`} data-testid={`invoice-row-${inv.id}`}>
      <div className="flex items-start gap-3">
        <div className="flex-1 min-w-0">
          <p className={`font-semibold leading-snug break-words ${cancelled ? "line-through" : ""}`}>{inv.supplier}</p>
          <p className="text-xs text-text-secondary mt-1 leading-relaxed">
            {catLabel(inv.category)}{inv.invoice_number ? ` · ${tx("inv.no")} ${inv.invoice_number}` : ""}{inv.invoice_date ? ` · ${tx("inv.of")} ${fmtDate(inv.invoice_date)}` : ""}
          </p>
          <p className="text-xs text-text-secondary leading-relaxed">
            {tx("tpl.due", { date: fmtDate(inv.due_date) })}{inv.paid_date ? ` · ${tx("tpl.paid_on", { date: fmtDate(inv.paid_date) })}` : ""}
          </p>
          {inv.tva_amount != null && inv.tva_amount > 0 && (
            <p className="text-[11px] text-secondary-c mt-1 font-medium" data-testid={`invoice-tva-${inv.id}`}>TVA {inv.tva_rate}% · {fmtCHF(inv.tva_amount)}</p>
          )}
          {inv.date_to_verify && !cancelled && (
            <p className="text-[11px] text-[#B45309] mt-1 flex items-center gap-1" data-testid={`invoice-date-verify-${inv.id}`}><AlertCircle className="w-3 h-3" />{tx("ui_invoicecard.kd54fc9")}</p>
          )}
          {cancelled && (
            <p className="text-[11px] text-text-secondary mt-1" data-testid={`invoice-cancel-info-${inv.id}`}>{tx("tpl.cancelled_on", { date: fmtDate((inv.cancelled_at || "").slice(0, 10)) })} · {inv.cancel_reason}</p>
          )}
        </div>
        <div className="text-right shrink-0">
          <p className={`font-display text-lg whitespace-nowrap ${cancelled ? "line-through" : ""}`}>{fmtCHF(inv.amount)}</p>
          <span className={`status-badge ${s.cls} mt-1`} data-testid={`invoice-status-${inv.id}`}>{s.txt}</span>
        </div>
      </div>
      {!cancelled && (
        <div className="flex items-center gap-1 mt-3 pt-2 border-t border-c flex-wrap">
          {inv.recurring && (
            <span className="text-[10px] font-semibold text-primary-c bg-[#E5F0EB] px-2 py-0.5 rounded-full flex items-center gap-1" data-testid={`invoice-recurring-badge-${inv.id}`}>
              <Repeat className="w-3 h-3" />{tx("ui_invoicecard.kb80b2c")}
            </span>
          )}
          <div className="flex items-center gap-1 ml-auto">
            {inv.paid ? (
              <Btn onClick={() => onAction("unpay", inv)} data-testid={`invoice-unpay-${inv.id}`} className="text-text-secondary hover:bg-surface-muted"><Undo2 className="w-4 h-4" /><span className="hidden min-[400px]:inline">{tx("ui_invoicecard.k2c782d")}</span></Btn>
            ) : (
              <Btn onClick={() => onAction("pay", inv)} data-testid={`invoice-pay-${inv.id}`} className="text-[#047857] hover:bg-[#E5F0EB]"><Check className="w-4 h-4" />{tx("ui_invoicecard.ke2567e")}</Btn>
            )}
            <button onClick={() => onDuplicate(inv.id)} data-testid={`invoice-duplicate-${inv.id}`} aria-label={tx("ui_invoicecard.k59fcf6")} className="w-10 h-10 rounded-xl text-text-secondary hover:bg-surface-muted flex items-center justify-center">
              <Copy className="w-4 h-4" />
            </button>
            <button onClick={() => onAction("cancel", inv)} data-testid={`invoice-cancel-${inv.id}`} aria-label={tx("ui_invoicecard.kb118c2")} className="w-10 h-10 rounded-xl text-text-secondary hover:bg-red-50 hover:text-red-600 flex items-center justify-center">
              <Ban className="w-4 h-4" />
            </button>
          </div>
        </div>
      )}
    </li>
  );
}
